import { useState, useEffect, useMemo, useCallback, type JSX } from 'react';
import { useShallow } from 'zustand/shallow';
import {
  Edit,
  Eye,
  Trash2,
  Plus,
  AlertTriangle,
  FileText,
  MoreHorizontal,
} from 'lucide-react';
import ReusableModal from '../../components/Modal';
import { useNavigate } from 'react-router-dom';
import '../risk-plus/report.css';
import '../UseCases/UseCases.css';

import { UseCaseBrief } from '../../entities/useCaseChat/UseCaseBrief';
import { ReportBrief } from '../../entities/report/ReportBrief';
import { ImpactReportDetailModal } from '../ImpactPlusReport/ImpactReportModal';
import { RiskReportDetailModal } from '../RiskPlusReport/RiskReportDetailModal';
import ImpactPlusReportStore from '../../stores/ImpactPlusReportStore';
import RiskPlusReportStore from '../../stores/RiskPlusReportStore';
import { DashboardStore } from '../../stores/DashboardStore';
import UseCaseChatStore from '../../stores/UseCaseChatStore';
import { UseCaseChat } from '../../entities/useCaseChat/UseCaseChat';
import { GenerateReportButton } from '../../components/GenerateReportButton';
import { AllUseCaseChatsCache } from '../../stores/AllUseCaseChatsCache';
import { ImpactPlusRegisterHeaderStore } from '../../stores/ImpactPlusRegisterHeaderStore';
import { type DecisionBucket } from './impactRegisterDummyData';
import { useReportModalParam } from '../../hooks/useReportModalParam';
type ReportTypeFilter = 'all' | 'impact' | 'risk';

function getDecisionBucket(brief: UseCaseBrief): DecisionBucket {
  if (brief.jobIsActive) return 'in_progress';
  const rank = brief.overallRiskRank;
  if (rank === 'critical') return 'not_recommended';
  if (rank === 'high') return 'significant_mitigation';
  if (rank === 'moderate') return 'staged_deployment';
  if (rank === 'low') return 'conditional_signoff';
  if (rank === 'negligible') return 'ready';
  return 'in_progress';
}

function getDecisionDisplay(brief: UseCaseBrief): { label: string; bucket: DecisionBucket } {
  const bucket = getDecisionBucket(brief);
  const labels: Record<DecisionBucket, string> = {
    not_recommended: 'Critical',
    significant_mitigation: 'High',
    staged_deployment: 'Moderate',
    conditional_signoff: 'Low',
    ready: 'Negligible',
    in_progress: 'Assessment in progress',
  };
  return { label: labels[bucket], bucket };
}

function hasImpactPlusReport(brief: UseCaseBrief): boolean {
  return (brief.reports ?? []).some(
    (r) =>
      /\bimpact\+?/i.test(String(r.reportTitle ?? '')) ||
      /impactplus/i.test(String(r.reportType ?? '')),
  );
}

function getFirstReportId(brief: UseCaseBrief): string | null {
  const report = (brief.reports ?? []).find(
    (r) =>
      /impactplus/i.test(String(r.reportType ?? '')) ||
      /\bimpact\+/i.test(String(r.reportTitle ?? '')),
  );
  return report?.reportId ? String(report.reportId) : null;
}


function hasRiskPlusReport(brief: UseCaseBrief): boolean {
  return (brief.reports ?? []).some((r) => /\brisk\+/i.test(String(r.reportTitle ?? '')));
}

function displayUseCaseTitle(title: string): string {
  if (title === '<untitled>') return '<untitled>';
  if (!title || title === 'N/A') return '<untitled>';
  return title;
}

const DecisionPill = ({ label, bucket }: { label: string; bucket: DecisionBucket }) => {
  const dot =
    bucket === 'not_recommended'
      ? 'var(--critical)'
      : bucket === 'significant_mitigation'
        ? 'var(--high)'
        : bucket === 'staged_deployment'
          ? 'var(--moderate)'
          : bucket === 'conditional_signoff'
            ? 'var(--low)'
            : bucket === 'ready'
              ? 'var(--risk-negligible)'
              : 'var(--risk-na)';
  return (
    <span className="inline-flex max-w-full items-start gap-1.5 rounded-[4px] bg-[#f4f4f4] px-2.5 py-1 text-xxs font-semibold leading-tight text-[#1f2937]">
      <span className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dot }} />
      <span className="min-w-0 break-words">{label}</span>
    </span>
  );
};

const StatusPill = ({ brief }: { brief: UseCaseBrief }) => {
  if (brief.jobIsActive) {
    return (
      <span className="inline-flex flex-wrap rounded-[5px] bg-[#ffe6c7] px-3 py-1 text-xxs font-semibold break-words leading-tight text-[#9a5a00]">
        In progress
      </span>
    );
  }
  if (brief.contentStatus === 'modified') {
    return (
      <span className="inline-flex flex-wrap rounded-[5px] bg-[#0070e0] px-3 py-1 text-xxs font-semibold break-words leading-tight text-white">
        Modified
      </span>
    );
  }
  if (brief.contentStatus === 'processed' || brief.contentStatus === 'expertValidated') {
    return (
      <span className="inline-flex flex-wrap rounded-[5px] bg-[#9eeeff] px-3 py-1 text-xxs font-semibold break-words leading-tight text-[#005a78]">
        Processed
      </span>
    );
  }
  if (brief.contentStatus === 'sufficient') {
    return (
      <span className="inline-flex flex-wrap rounded-[5px] bg-[#d8f5ff] px-3 py-1 text-xxs font-semibold break-words leading-tight text-[#0070e0]">
        Ready for processing
      </span>
    );
  }
  if (brief.contentStatus === 'none' || brief.contentStatus === 'partial') {
    return (
      <span className="inline-flex flex-wrap rounded-[5px] bg-[#f4f4f4] px-3 py-1 text-xxs font-semibold break-words leading-tight text-[#6b7280]">
        Insufficient Content
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap rounded-[5px] bg-[#f4f4f4] px-3 py-1 text-xxs font-semibold break-words leading-tight text-gray-600">
      Pending
    </span>
  );
};

// const SkeletonRow = () => (
//   <tr className="border-b border-gray-200">
//     <td className="py-4 pl-5 pr-3 align-top">
//       <div className="usecases-skeleton mb-2 h-5 w-3/4 max-w-xs" />
//       <div className="usecases-skeleton h-4 w-full max-w-sm" />
//       <div className="mt-2 flex gap-2">
//         <div className="usecases-skeleton h-6 w-16 rounded-[20px]" />
//         <div className="usecases-skeleton h-6 w-20 rounded-[20px]" />
//       </div>
//     </td>
//     <td className="py-4 px-3 align-top text-center">
//       <div className="usecases-skeleton mx-auto h-7 w-24 rounded-[20px]" />
//     </td>
//     <td className="py-4 px-3 align-top text-center">
//       <div className="usecases-skeleton mx-auto h-8 w-36 rounded-[20px]" />
//     </td>
//     <td className="py-4 px-3 align-top text-center">
//       <div className="mx-auto flex max-w-[13rem] flex-col items-center gap-1">
//         <div className="usecases-skeleton h-7 w-full max-w-[11rem] rounded-[20px]" />
//         <div className="usecases-skeleton h-4 w-28" />
//       </div>
//     </td>
//     <td className="py-4 px-4 align-top text-center">
//       <div className="flex justify-center gap-1">
//         <div className="usecases-skeleton h-9 w-9 rounded-full" />
//         <div className="usecases-skeleton h-9 w-9 rounded-full" />
//         <div className="usecases-skeleton h-9 w-9 rounded-full" />
//       </div>
//     </td>
//   </tr>
// );

const EmptyState = ({
  onCreate,
  ctaDisabled = false,
  hint,
}: {
  onCreate: () => void;
  ctaDisabled?: boolean;
  hint?: string;
}): JSX.Element => (
  <div className="empty-state flex flex-col items-center justify-center px-6 py-16 text-center">
    <div
      className="empty-title mb-1.5 text-sm font-semibold tracking-[-0.2px]"
      style={{ color: '#1A1A18' }}
    >
      No use cases registered yet
    </div>
    <div
      className="empty-desc mb-5 max-w-[300px] text-xs leading-[1.65]"
      style={{ color: '#999893' }}
    >
      Once you create and assess your first AI use case, it will appear here with its deployment
      decision and report status.
    </div>
    <button
      type="button"
      onClick={onCreate}
      disabled={ctaDisabled}
      className="empty-cta inline-flex items-center cursor-pointer gap-1.5 rounded-[8px] px-5 py-[9px] bg-midnight-navi text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
    >
      <Plus size={14} strokeWidth={2.2} />
      Create your first use case
    </button>
    {hint ? (
      <div className="empty-hint mt-2.5 text-xxs" style={{ color: '#C8C6C0' }}>
        {hint}
      </div>
    ) : null}
  </div>
);

const ImpactPlusReport = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const impactReportModal = useReportModalParam('impactPlusReportModal');
  const riskReportModal = useReportModalParam('riskPlusReportModal');
  const [reportTypeFilter, setReportTypeFilter] = useState<ReportTypeFilter>('all');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  const registerSearchQuery = ImpactPlusRegisterHeaderStore((s) => s.registerSearchQuery);
  const setRegisterSearchQuery = ImpactPlusRegisterHeaderStore((s) => s.setRegisterSearchQuery);

  const navigate = useNavigate();

  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalChildren, setModalChildren] = useState<React.ReactNode>(null);
  const [modalAction, setModalAction] = useState<() => void>(() => { });
  const [actionButtonText, setActionButtonText] = useState<string>('Action');

  const isLoadingUseCaseBriefsFromStore = DashboardStore.useDashboardStore(
    (state) => state.isLoadingUseCaseBriefs,
  );
  const loadUseCaseBriefs = DashboardStore.useDashboardStore(
    (state) => state.loadUseCaseBriefs,
  );
  const useCaseBriefsFromStore = DashboardStore.useDashboardStore((state) => state.useCaseBriefs);
  const isLoadingUseCaseBriefs = isLoadingUseCaseBriefsFromStore;
  const useCaseBriefs = useCaseBriefsFromStore;
  const deleteUseCase = DashboardStore.useDashboardStore((state) => state.deleteUseCase);

  const useCaseChats = AllUseCaseChatsCache.useAllUseCaseChatsCache(
    (state) => state.useCaseChats,
  );

  const createUseCaseChat = UseCaseChatStore.useUseCaseChatStore(
    (state) => state.createUseCaseChat,
  );

  const { loadRiskPlusReport, setRiskPlusReport } = RiskPlusReportStore.useRiskPlusReportStore(
    useShallow((state) => ({
      loadRiskPlusReport: state.loadReportData,
      setRiskPlusReport: state.setRiskPlusReport,
    })),
  );

  const { loadImpactPlusReport, setImpactPlusReport } = ImpactPlusReportStore.useImpactPlusReportStore(
    useShallow((state) => ({
      loadImpactPlusReport: state.loadImpactPlusReport,
      setImpactPlusReport: state.setImpactPlusReport,
    })),
  );

  const filteredBriefs = useMemo(() => {
    const q = registerSearchQuery.trim().toLowerCase();
    return useCaseBriefs.filter((b) => {
      if (reportTypeFilter === 'impact' && !hasImpactPlusReport(b)) return false;
      if (reportTypeFilter === 'risk' && !hasRiskPlusReport(b)) return false;
      if (!q) return true;
      const title = displayUseCaseTitle(b.useCaseTitle).toLowerCase();
      if (title.includes(q)) return true;
      return (
        Array.isArray(b.reports) &&
        b.reports.some((r) => (r.reportTitle || '').toLowerCase().includes(q))
      );
    });
  }, [useCaseBriefs, reportTypeFilter, registerSearchQuery]);

  useEffect(() => {
    if (!menuOpenId) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (!el.closest(`[data-register-menu-root="${menuOpenId}"]`)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpenId]);

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleImpactReportModalClose = () => {
    impactReportModal.close();
    setImpactPlusReport(null);
  };

  const handleRiskReportModalClose = () => {
    riskReportModal.close();
    setRiskPlusReport(null);
  };

  const isImpactReport = (report: ReportBrief): boolean => {
    const type = String(report.reportType ?? '').toLowerCase();
    const title = String(report.reportTitle ?? '').toLowerCase();
    return /impactplus/.test(type) || /impact[\s+]/.test(title) || /impact\+/.test(title);
  };

  const handleViewReport = (report: ReportBrief) => {
    if (isImpactReport(report)) {
      impactReportModal.open(report.reportId);
    } else {
      riskReportModal.open(report.reportId);
    }
  };

  useEffect(() => {
    if (!impactReportModal.reportId) return;
    void loadImpactPlusReport(impactReportModal.reportId).catch((error) => {
      console.error('Error fetching report data:', error);
    });
  }, [impactReportModal.reportId, loadImpactPlusReport]);

  useEffect(() => {
    if (!riskReportModal.reportId) return;
    void loadRiskPlusReport(riskReportModal.reportId).catch((error) => {
      console.error('Error fetching report data:', error);
    });
  }, [riskReportModal.reportId, loadRiskPlusReport]);

  const anyReportModalOpen = impactReportModal.isOpen || riskReportModal.isOpen;

  useEffect(() => {
    if (anyReportModalOpen) return;
    void loadUseCaseBriefs();
    const interval = window.setInterval(() => {
      void loadUseCaseBriefs(true).catch(() => { });
    }, 20000);
    return () => window.clearInterval(interval);
  }, [loadUseCaseBriefs, anyReportModalOpen]);

  const getUseCaseSourcePath = (): string => {
    return '/dashboard';
  };

  const openUseCaseOverview = (id: string, impactPlusReportId?: string | null) => {
    const path = `/use-case/${id}`;
    if (impactPlusReportId) {
      navigate(`${path}?impactPlusReportId=${encodeURIComponent(impactPlusReportId)}`);
      return;
    }
    navigate(path);
  };

  const handleEdit = async (id: string) => {
    try {
      navigate(`/chat/${id}/edit`, {
        state: { from: getUseCaseSourcePath() },
      });
    } catch (err) {
      console.error('Error redirecting to chat:', err);
      setModalTitle('Error');
      setModalChildren(
        <div className="text-red-600">
          <p>Could not open use case for editing.</p>
          <p className="text-xxs mt-2">Please try again or contact support.</p>
        </div>,
      );
      setActionButtonText('Close');
      setModalAction(() => {
        handleModalClose();
      });
      setShowModal(true);
    }
  };

  const handleDelete = async (id: string) => {
    setMenuOpenId(null);
    try {
      setModalTitle('');
      setModalChildren(
        <div className="py-6 px-2">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="mb-4 flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Delete Use Case?
            </h3>
            <p className="text-gray-600 text-xxs leading-relaxed max-w-md">
              Are you sure you want to delete this use case? This action cannot be undone and all
              associated data will be permanently removed.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleModalClose}
              className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow"
            >
              Cancel
            </button>
            <button
              onClick={() => handleConfirmDelete(id)}
              className="px-6 py-2.5 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow-md"
            >
              Delete
            </button>
          </div>
        </div>,
      );
      setActionButtonText('');
      setModalAction(() => { });
      setShowModal(true);
    } catch (err) {
      console.error('Error deleting use case:', err);
    }
  };

  const handleConfirmDelete = async (id: string) => {
    if (!id) return;
    try {
      await deleteUseCase(id);
      handleModalClose();
    } catch (err) {
      console.error('Error deleting use case:', err);
    }
  };

  const handleCreateNewFromButton = useCallback(async () => {
    try {
      const id = await createUseCaseChat();

      const sourcePath = '/dashboard';
      const modeSuffix = 'new-impact';

      navigate(`/chat/${id}/${modeSuffix}`, {
        state: { from: sourcePath },
      });

      await loadUseCaseBriefs(true).catch(() => { });
    } catch (error) {
      console.error('Error initializing use case chat:', error);
      navigate(`/chat/n`);
    }
  }, [createUseCaseChat, navigate, loadUseCaseBriefs]);

  const setCreateNewHandler = ImpactPlusRegisterHeaderStore((s) => s.setCreateNewHandler);

  useEffect(() => {
    setCreateNewHandler(handleCreateNewFromButton);
    return () => {
      setCreateNewHandler(null);
      ImpactPlusRegisterHeaderStore.getState().setRegisterSearchQuery('');
    };
  }, [handleCreateNewFromButton, setCreateNewHandler]);

  const renderPendingDecision = (useCaseBrief: UseCaseBrief): JSX.Element | null => {
    if (useCaseBrief.jobIsActive || useCaseBrief.overallRiskRank !== 'n/a') return null;
    return (
      <span className="text-xxs italic leading-none text-[#8b8b8b]">
        Not yet generated
      </span>
    );
  };

  const getUseCaseReportContext = (useCaseBrief: UseCaseBrief) => {
    const { reports } = useCaseBrief;
    const useCaseChat: UseCaseChat =
      useCaseChats.find((ucc) => ucc.id === useCaseBrief.useCaseChatId) ||
      new UseCaseChat();
    const isJobActive = useCaseBrief.jobIsActive;
    const isModified = useCaseBrief.contentStatus === 'modified';

    const isRiskReportItem = (r: ReportBrief) =>
      /riskplus/i.test(String(r.reportType ?? '')) || /risk\+/i.test(String(r.reportTitle ?? ''));

    const riskReport = reports.find(isRiskReportItem) ?? null;
    const impactReport = reports.find(isImpactReport) ?? null;
    const contentIsOk = useCaseChat.isSufficientForReportGeneration;

    return { useCaseChat, isJobActive, isModified, riskReport, impactReport, contentIsOk };
  };

  const reportLink = (report: ReportBrief, label: string, elId?: string) => (
    <button
      key={report.reportId}
      id={elId}
      className="inline-flex max-w-full items-center justify-start gap-1.5 whitespace-normal text-left text-xxs font-semibold text-[#0070e0] hover:text-deep-blue hover:underline"
      onClick={() => handleViewReport(report)}
    >
      <FileText size={13} className="shrink-0" />
      <span className="min-w-0 truncate">{label}</span>
    </button>
  );

  const renderReportLinks = (useCaseBrief: UseCaseBrief, isFirstRow: boolean = false) => {
    const { statusMessage, status } = useCaseBrief;

    switch (status) {
      case 'processing':
        return (
          <span className="block max-w-full truncate text-xxs text-gray-500">{statusMessage}</span>
        );
      case 'queued':
        return <span className="text-xxs text-gray-500 whitespace-nowrap">queued</span>;
      case 'paused':
        return <span className="text-xxs text-gray-500 whitespace-nowrap">paused</span>;
      default:
    }

    const { riskReport, impactReport } = getUseCaseReportContext(useCaseBrief);

    if (!riskReport && !impactReport) {
      return <span className="text-xxs italic leading-none text-[#8b8b8b]">No reports yet</span>;
    }

    return (
      <div className="flex w-full flex-col items-start gap-3">
        {riskReport && reportLink(riskReport, 'Risk+ Report', isFirstRow ? 'first-report-link' : undefined)}
        {impactReport && reportLink(impactReport, 'Impact+ Report', isFirstRow && !riskReport ? 'first-report-link' : undefined)}
      </div>
    );
  };

  const renderGenerateActions = (useCaseBrief: UseCaseBrief) => {
    const { status } = useCaseBrief;
    if (status === 'processing' || status === 'queued' || status === 'paused') return null;

    const { useCaseChat, isJobActive, isModified, riskReport, impactReport, contentIsOk } =
      getUseCaseReportContext(useCaseBrief);

    const disabledButton = (key: string, label: string) => (
      <button
        key={key}
        type="button"
        disabled
        className="inline-flex h-auto min-h-7 cursor-not-allowed items-center justify-center rounded-[6px] bg-[#e6e6e6] px-4 py-1 text-xxs font-semibold leading-tight text-[#9b9b9b] whitespace-normal text-center"
      >
        {label}
      </button>
    );

    const genButton = (type: 'risk' | 'impact', label: string, variant: 'solid' | 'outline' = 'solid') => {
      if (!contentIsOk) {
        return disabledButton(`${type}-disabled`, label);
      }
      return (
        <GenerateReportButton
          key={`${type}-${variant}`}
          useCaseChat={useCaseChat}
          label={label}
          reportType={type}
          variant={variant}
          disabled={isJobActive}
        />
      );
    };

    const buttons: JSX.Element[] = [];
    if (!riskReport) {
      buttons.push(genButton('risk', 'Generate Risk+ Report'));
    } else if (isModified) {
      buttons.push(genButton('risk', 'Refresh Risk+ Report', 'outline'));
    } else {
      buttons.push(disabledButton('risk-refresh-disabled', 'Refresh Risk+ Report'));
    }
    if (!impactReport) {
      buttons.push(genButton('impact', 'Generate Impact+ Report'));
    } else if (isModified) {
      buttons.push(genButton('impact', 'Refresh Impact+ Report', 'outline'));
    } else {
      buttons.push(disabledButton('impact-refresh-disabled', 'Refresh Impact+ Report'));
    }

    if (buttons.length === 0) return null;

    return <div className="flex w-full flex-col items-start gap-3">{buttons}</div>;
  };

  const useCaseCountLabel =
    useCaseBriefs.length === 1 ? '1 use case' : `${useCaseBriefs.length} use cases`;

  const renderRegisterBody = () => {
    if (isLoadingUseCaseBriefs) {
      return (
        <div className="py-14 px-6 text-center">
          <p className="text-gray-400 text-xs">Loading...</p>
        </div>
      );
    }

    if (useCaseBriefs.length === 0) {
      return <EmptyState onCreate={handleCreateNewFromButton} />;
    }

    if (filteredBriefs.length === 0) {
      const hasSearch = registerSearchQuery.trim().length > 0;
      return (
        <div className="py-14 px-6 text-center">
          <p className="text-gray-600 mb-4">
            {hasSearch ? 'No use cases match your search.' : 'No use cases match this filter.'}
          </p>
          <button
            type="button"
            onClick={() => {
              setReportTypeFilter('all');
              setRegisterSearchQuery('');
            }}
            className="text-xxs font-semibold text-royal-blue hover:text-deep-blue hover:underline"
          >
            {hasSearch ? 'Clear search and filters' : 'Show all use cases'}
          </button>
        </div>
      );
    }

    return (
      <div id="impact-register-table">
        <div className="hidden lg:block overflow-x-auto">
          <table className="impact-register-table w-full min-w-[950px] table-fixed">
            <thead>
              <tr className="border-b border-[#e5e5e5] bg-[#f5f5f5]">
                <th className="w-[28%] py-3 pl-5 pr-3 text-left align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Use case
                </th>
                <th className="w-[12%] px-3 py-3 text-left align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Outcome
                </th>
                <th className="w-[16%] px-3 py-3 text-left align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Status
                </th>
                <th className="w-[20%] px-3 py-3 text-left align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Generate
                </th>
                <th className="w-[14%] px-3 py-3 text-left align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Reports
                </th>
                <th className="w-[10%] min-w-[7rem] px-4 py-3 text-center align-middle text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b] whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredBriefs.map((useCase, idx) => {
                const decision = getDecisionDisplay(useCase);
                const pendingDecision = renderPendingDecision(useCase);
                const title = displayUseCaseTitle(useCase.useCaseTitle);
                const isUntitled = title === '<untitled>';
                return (
                  <tr
                    key={useCase.useCaseChatId}
                    id={idx === 0 ? 'first-reg-row' : undefined}
                    className="pt-2 pb-2 border-b border-soft-gray bg-white last:border-0 hover:bg-[#fbfbfb] transition-colors cursor-pointer"
                    onClick={() => openUseCaseOverview(useCase.useCaseChatId, getFirstReportId(useCase))}
                  >
                    <td className="py-3 pl-5 pr-3 align-middle">
                      <div
                        className={`min-w-0 text-xs break-words whitespace-normal leading-tight ${isUntitled
                          ? 'font-medium italic text-[#8b8b8b]'
                          : 'font-semibold text-[#111827]'
                          }`}
                      >
                        {title}
                      </div>
                    </td>
                    <td className="py-3 px-3 align-middle text-left">
                      <div className="flex min-w-0 max-w-full justify-start flex-wrap">
                        {pendingDecision ?? (
                          <DecisionPill label={decision.label} bucket={decision.bucket} />
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 align-middle text-left">
                      <div className="flex justify-start flex-wrap">
                        <StatusPill brief={useCase} />
                      </div>
                    </td>
                    <td
                      className="py-3 px-3 align-middle text-left"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex min-w-0 flex-col items-start justify-center gap-1">
                        {renderGenerateActions(useCase)}
                      </div>
                    </td>
                    <td
                      className="py-3 px-3 align-middle text-left"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex min-w-0 flex-col items-start justify-center gap-1">
                        {renderReportLinks(useCase, idx === 0)}
                      </div>
                    </td>
                    <td className="py-3 px-4 align-middle text-center" onClick={(e) => e.stopPropagation()}>
                      <div
                        className="flex justify-center gap-2"
                        data-register-menu-root={useCase.useCaseChatId}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            openUseCaseOverview(
                              useCase.useCaseChatId,
                              getFirstReportId(useCase),
                            )
                          }
                          className="usecases-action-btn inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-[#bdefff] bg-[#d8f5ff] text-[#0070e0] hover:bg-[#c7f1ff]"
                          aria-label={`View ${title}`}
                        >
                          <Eye size={14} />
                        </button>
                        {!useCase.jobIsActive && (
                          <button
                            type="button"
                            onClick={() => handleEdit(useCase.useCaseChatId)}
                            className="usecases-action-btn inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-[#e5e5e5] bg-white text-[#4b5563] hover:bg-[#f7f7f7]"
                            aria-label={`Edit ${title}`}
                          >
                            <Edit size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="usecases-action-btn inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] border border-[#e5e5e5] bg-white text-[#4b5563] hover:bg-[#f7f7f7]"
                          onClick={() => handleDelete(useCase.useCaseChatId)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="lg:hidden divide-y divide-gray-200">
          {filteredBriefs.map((useCase, idx) => {
            const decision = getDecisionDisplay(useCase);
            const pendingDecision = renderPendingDecision(useCase);
            const title = displayUseCaseTitle(useCase.useCaseTitle);
            const isUntitled = title === '<untitled>';
            return (
              <div
                key={useCase.useCaseChatId}
                id={idx === 0 ? 'first-reg-row' : undefined}
                className="usecases-card p-4 active:bg-gray-50 cursor-pointer"
                data-register-menu-root={useCase.useCaseChatId}
                onClick={() => openUseCaseOverview(useCase.useCaseChatId, getFirstReportId(useCase))}
              >
                <div className="mb-3">
                  <div
                    className={`text-xs line-clamp-2 ${isUntitled ? 'font-medium italic text-[#8b8b8b]' : 'font-bold text-gray-900'
                      }`}
                  >
                    {title}
                  </div>
                </div>
                <div className="mb-3 flex flex-wrap items-center justify-center gap-2">
                  {pendingDecision ?? (
                    <DecisionPill label={decision.label} bucket={decision.bucket} />
                  )}
                  <StatusPill brief={useCase} />
                </div>
                <div
                  className="mb-3 flex min-h-10 flex-col items-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mb-1 text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b]">Reports</div>
                  {renderReportLinks(useCase, idx === 0)}
                </div>
                {renderGenerateActions(useCase) && (
                  <div
                    className="mb-4 flex flex-col items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="mb-1 text-xxxs font-medium uppercase tracking-wide text-[#8b8b8b]">Generate</div>
                    {renderGenerateActions(useCase)}
                  </div>
                )}
                <div
                  className="flex items-center justify-end gap-2 border-t border-gray-200 pt-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() =>
                      openUseCaseOverview(
                        useCase.useCaseChatId,
                        getFirstReportId(useCase),
                      )
                    }
                    className="usecases-action-btn flex items-center gap-1.5 rounded-lg border border-blue-200 bg-sky-50 px-3 py-2 text-xxs font-medium text-blue-600 hover:bg-sky-100"
                  >
                    <Eye size={16} /> View
                  </button>
                  {!useCase.jobIsActive && (
                    <button
                      type="button"
                      onClick={() => handleEdit(useCase.useCaseChatId)}
                      className="usecases-action-btn flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xxs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Edit size={16} /> Edit
                    </button>
                  )}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setMenuOpenId((id) =>
                          id === useCase.useCaseChatId ? null : useCase.useCaseChatId,
                        )
                      }
                      className="usecases-action-btn flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xxs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <MoreHorizontal size={16} /> More
                    </button>
                    {menuOpenId === useCase.useCaseChatId && (
                      <div className="absolute bottom-full right-0 z-20 mb-1 w-44 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xxs text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(useCase.useCaseChatId)}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  return (
    <div className="min-h-screen bg-[#fafafa] p-4 md:p-6 lg:p-6">
      <div className="mx-auto max-w-none">
        <div className="overflow-hidden rounded-[6px] border border-[#e5e5e5] bg-white shadow-none">
          <div className="border-b border-[#e5e5e5] px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              <h1
                id="reg-panel-title"
                className="min-w-0 text-sm font-semibold leading-none text-[#111827]"
              >
                Use Case Register
              </h1>
              <div className="flex items-center gap-2">
                {(useCaseBriefs.length > 0 && !isLoadingUseCaseBriefs) && (
                  <span className="inline-flex shrink-0 items-center rounded-[6px] border border-[#e5e5e5] bg-white px-3 py-1 text-xxxs font-medium leading-none text-[#7a7a7a]">
                    {useCaseCountLabel}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* {((useCaseBriefs.length > 0 && !isLoadingUseCaseBriefs) || NO_DATA_MODE) && (
            <div className="border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-2.5">
              <div className="flex flex-col gap-2.5">
                <div id="type-filter-bar" className="flex w-full flex-wrap items-center gap-2">
                  <span className="shrink-0 text-xxs font-medium text-[#6b7280]">Report type:</span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setReportTypeFilter('all')}
                      className={`rounded-[3px] px-3 py-1.5 text-xxs font-medium leading-none transition-colors ${reportTypePillClass('all')}`}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setReportTypeFilter('impact')}
                      className={`rounded-[3px] px-3 py-1.5 text-xxs font-medium leading-none transition-colors ${reportTypePillClass('impact')}`}
                    >
                      Impact+
                    </button>
                    <button
                      type="button"
                      onClick={() => setReportTypeFilter('risk')}
                      className={`rounded-[3px] px-3 py-1.5 text-xxs font-medium leading-none transition-colors ${reportTypePillClass('risk')}`}
                    >
                      Risk+
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )} */}

          {renderRegisterBody()}
        </div>


        <ReusableModal
          title={modalTitle}
          isOpen={showModal}
          onClose={handleModalClose}
          onAction={modalAction}
          actionButtonText={actionButtonText}
          closeButtonText="Cancel"
          size="md"
        >
          {modalChildren}
        </ReusableModal>

        <ImpactReportDetailModal isOpen={impactReportModal.isOpen} onClose={handleImpactReportModalClose} />
        <RiskReportDetailModal isOpen={riskReportModal.isOpen} onClose={handleRiskReportModalClose} />
      </div>
    </div>
  );
};

export default ImpactPlusReport;
